import React, { useState, useEffect } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import PageHeader from '../../components/PageHeader';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { clearApplicationDetails, setAppName, setAppStatus, setPrimaryColor, setSecondaryColor, toggleCallAnalyticsEnabled, toggleCallsEnabled, toggleChatAnalyticsEnabled, toggleChatsEnabled, toggleChatBotEnabled, toggleExportDataAccess, toggleNotificationsEnabled, toggleScheduledCallsEnabled, toggleSentimentAnalysisEnabled, toggleUserAnalyticsEnabled } from '../../features/application/applicationDetailsSlice';

// Import our extracted components
import {
  GeneralSettings,
  ThemeSettings,
  DataPrivacySettings as DataPrivacySettingsComponent,
  SecuritySettings,
  StorageSettings,
  AdvancedSettings as AdvancedSettingsComponent,
  DeleteApplicationDialog,
  ApplicationTabNavigation,
  ROLE_PERMISSIONS,
  UserRole,
  PermissionUtils,
  ApplicationInfo,
  ApplicationThemeSettings,
  DataPrivacySettingsType,
  SecuritySettingsType,
  AdvancedSettingsType,
} from '../../components/application';
import { decryptResponse, encryptResponse, getSessionKey } from '../../services';
import OtpInput from '../../components/common/OtpInput';

const ApplicationSettings: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [otp, setOtp] = useState('');
  const [isOtpPopupOpen, setIsOtpPopupOpen] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('Sending OTP...');
  const dispatch = useDispatch();

  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const applicationDetails = useSelector((state: any) => state.applicationDetails);

  // Get user role and permissions
  const userRole = userDetails?.role as UserRole || 'Desk Agent';
  const userPermissions = ROLE_PERMISSIONS[userRole] || ROLE_PERMISSIONS['Desk Agent'];

  useEffect(() => {
    if (applicationDetails) {
      setApplicationInfo(prev => ({
        ...prev,
        app_id: applicationDetails.app_id || '',
        name: applicationDetails.app_name || '',
        app_status: applicationDetails.app_status || '',
        is_calls_enabled: applicationDetails.is_calls_enabled ?? true,
        is_chats_enabled: applicationDetails.is_chats_enabled ?? true,
        is_chat_bot_enabled: applicationDetails.is_chat_bot_enabled ?? false,
        is_notifications_enabled: applicationDetails.is_notifications_enabled ?? true,
        is_scheduled_calls_enabled: applicationDetails.is_scheduled_calls_enabled ?? true
      }));
      setThemeSettings({
        primaryColor: applicationDetails.primary_color || '#1976d2',
        secondaryColor: applicationDetails.secondary_color || '#dc004e'
      });
      setDataPrivacySettings({
        allowDataAccess: applicationDetails.is_export_data_access_enabled ?? false,
        accessUserAnalytics: applicationDetails.is_user_analytics_enabled ?? false,
        accessChatAnalytics: applicationDetails.is_chat_analytics_enabled ?? false,
        accessCallAnalytics: applicationDetails.is_call_analytics_enabled ?? false,
        accessSentimentAnalysis: applicationDetails.is_sentiment_analysis_enabled ?? false
      });
    }
  }, [applicationDetails]);

  // Permission utility functions
  const canAccessSection = (section: keyof typeof ROLE_PERMISSIONS['Admin']) => {
    return userPermissions[section] &&
      (typeof userPermissions[section] === 'boolean'
        ? userPermissions[section]
        : (userPermissions[section] as any)?.read);
  };

  const canEditSection = (section: keyof typeof ROLE_PERMISSIONS['Admin']) => {
    return userPermissions[section] &&
      (typeof userPermissions[section] === 'boolean'
        ? userPermissions[section]
        : (userPermissions[section] as any)?.write);
  };

  const canPerformAction = (action: keyof typeof ROLE_PERMISSIONS['Admin']) => {
    return Boolean(userPermissions[action]);
  };

  // Permission utilities object
  const permissionUtils: PermissionUtils = {
    canAccessSection,
    canEditSection,
    canPerformAction
  };

  // Set default active tab to first available tab based on permissions
  useEffect(() => {
    const firstAvailableTab = ['general', 'theme', 'privacy', 'security', 'storage', 'advanced'].find(tab =>
      canAccessSection(tab as any)
    );
    if (firstAvailableTab && !canAccessSection(activeTab as any)) {
      setActiveTab(firstAvailableTab);
    }
  }, [userRole, activeTab, canAccessSection]);
  const [applicationInfo, setApplicationInfo] = useState<ApplicationInfo>({
    app_id: applicationDetails?.app_id || '',
    name: applicationDetails?.app_name || '',
    app_status: applicationDetails?.app_status || '',
    is_calls_enabled: applicationDetails?.is_calls_enabled ?? true,
    is_chats_enabled: applicationDetails?.is_chats_enabled ?? true,
    is_notifications_enabled: applicationDetails?.is_notifications_enabled ?? true,
    is_scheduled_calls_enabled: applicationDetails?.is_scheduled_calls_enabled ?? true,
    is_chat_bot_enabled: applicationDetails?.is_chat_bot_enabled ?? false
  });

  const [themeSettings, setThemeSettings] = useState<ApplicationThemeSettings>({
    primaryColor: applicationDetails?.primary_color || '#1976d2',
    secondaryColor: applicationDetails?.secondary_color || '#dc004e'
  });

  const [dataPrivacySettings, setDataPrivacySettings] = useState<DataPrivacySettingsType>({
    allowDataAccess: applicationDetails?.is_export_data_access_enabled ?? false,
    accessUserAnalytics: applicationDetails?.is_user_analytics_enabled ?? false,
    accessChatAnalytics: applicationDetails?.is_chat_analytics_enabled ?? false,
    accessCallAnalytics: applicationDetails?.is_call_analytics_enabled ?? false,
    accessSentimentAnalysis: applicationDetails?.is_sentiment_analysis_enabled ?? false
  });

  const [securitySettings, setSecuritySettings] = useState<SecuritySettingsType>({
    allowedDomains: [],
    allowedIPs: []
  });

  const [advancedSettings, setAdvancedSettings] = useState<AdvancedSettingsType>({
    profanityFilterEnabled: false,
    profanityWords: [],
    regexPatterns: []
  });

  useEffect(() => {
    if (applicationDetails) {
      setApplicationInfo(prev => ({
        ...prev,
        app_id: applicationDetails.app_id || prev.app_id,
        name: applicationDetails.app_name || prev.name,
        app_status: applicationDetails.app_status || prev.app_status,
        is_calls_enabled: applicationDetails.is_calls_enabled ?? prev.is_calls_enabled,
        is_chat_bot_enabled: applicationDetails.is_chat_bot_enabled ?? prev.is_chat_bot_enabled,
        is_chats_enabled: applicationDetails.is_chats_enabled ?? prev.is_chats_enabled,
        is_notifications_enabled: applicationDetails.is_notifications_enabled ?? prev.is_notifications_enabled,
        is_scheduled_calls_enabled: applicationDetails.is_scheduled_calls_enabled ?? prev.is_scheduled_calls_enabled
      }));
      setThemeSettings(prev => ({
        primaryColor: applicationDetails.primary_color || prev.primaryColor,
        secondaryColor: applicationDetails.secondary_color || prev.secondaryColor
      }));
    }
  }, [applicationDetails]);

  const handleSaveTheme = async (): Promise<boolean> => {
    try {
      const payload = {
        data: await encryptResponse({
          primary_color: themeSettings.primaryColor,
          secondary_color: themeSettings.secondaryColor,
          organization_id: userDetails?.organization_id || '',
          user_id: userDetails?.user_id || '',
          app_id: applicationDetails?.app_id || ''
        }, REQ_ENC_KEY),
      };
      const response = await httpService.post(`${API_BASE_URL}/user/updateThemeSettings`, payload);
      const responseData = response.data as { status?: number; data?: any };
      if (responseData?.status === 200) {
        let primaryColor = themeSettings.primaryColor;
        let secondaryColor = themeSettings.secondaryColor;
        if (responseData.data) {
          const decryptedData = await decryptResponse<any>(responseData.data, getSessionKey() as string);
          const themeData = decryptedData?.updatedThemeSettings
            || decryptedData?.themeSettings
            || decryptedData?.application
            || decryptedData;
          if (themeData?.primary_color) {
            primaryColor = themeData.primary_color;
          }
          if (themeData?.secondary_color) {
            secondaryColor = themeData.secondary_color;
          }
        }
        dispatch(setPrimaryColor(primaryColor));
        dispatch(setSecondaryColor(secondaryColor));
        setThemeSettings({
          primaryColor,
          secondaryColor
        });
        toast.success('Theme settings saved successfully!');
        return true;
      }
      toast.error('Failed to save theme settings');
      return false;
    } catch (error) {
      toast.error('Failed to save theme settings');
      return false;
    }
  };

  const handleSaveDataPrivacy = async () => {
    try {
      const payload = {
        data: await encryptResponse({
          allow_data_access: dataPrivacySettings.allowDataAccess,
          access_user_analytics: dataPrivacySettings.accessUserAnalytics,
          access_chat_analytics: dataPrivacySettings.accessChatAnalytics,
          access_call_analytics: dataPrivacySettings.accessCallAnalytics,
          access_sentiment_analysis: dataPrivacySettings.accessSentimentAnalysis,
          organization_id: userDetails?.organization_id || '',
          user_id: userDetails?.user_id || '',
          app_id: applicationDetails?.app_id || ''
        }, REQ_ENC_KEY),
      };
      const response = await httpService.post(`${API_BASE_URL}/user/updateDataPrivacySettings`, payload);
      const responseData = response.data as { status?: number, data?: any };
      if (responseData.status === 200 && responseData.data) {
        const decryptedData = await decryptResponse<any>(responseData.data, getSessionKey() as string);
        const resData = decryptedData?.updatedDataPrivacySettings;
        if (resData) {
          dispatch(toggleExportDataAccess(resData.allow_data_access));
          dispatch(toggleUserAnalyticsEnabled(resData.access_user_analytics));
          dispatch(toggleChatAnalyticsEnabled(resData.access_chat_analytics));
          dispatch(toggleCallAnalyticsEnabled(resData.access_call_analytics));
          dispatch(toggleSentimentAnalysisEnabled(resData.access_sentiment_analysis));
          toast.success('Data & Privacy settings saved successfully!');
        }
        else {
          toast.error('Failed to save Data & Privacy settings');
        }
      } else {
        toast.error('Failed to save Data & Privacy settings');
      }
    } catch (error) {
      toast.error('Failed to save Data & Privacy settings');
    }
  };

  const handleSaveSecurity = async () => {
    try {
      const payload = {
        data: await encryptResponse({
          allowed_domains: securitySettings.allowedDomains,
          allowed_ips: securitySettings.allowedIPs,
          organization_id: userDetails?.organization_id || '',
          user_id: userDetails?.user_id || '',
          app_id: applicationDetails?.app_id || ''
        }, REQ_ENC_KEY),
      };
      const response = await httpService.post(`${API_BASE_URL}/user/updateSecuritySettings`, payload);
      const responseData = response.data as { status?: number; data?: string };
      if (responseData.status === 200 && responseData.data) {
        const decryptedData = await decryptResponse<any>(responseData.data, getSessionKey() as string);
        const resData = decryptedData?.updatedSecuritySettings;
        if (resData) {
          toast.success('Security & Privacy settings saved successfully!');
        }
        else {
          toast.error('Failed to save Security & Privacy settings');
        }
      } else {
        toast.error('Failed to save Security & Privacy settings');
      }
    } catch (error) {
      toast.error('Failed to save Security & Privacy settings');
    }
  };

  const handleSaveAdvanced = async () => {
    try {
      setIsLoading(true);

      const requestBody = {
        data: await encryptResponse({
          organization_id: userDetails?.organization_id || '',
          user_id: userDetails?.user_id || '',
          app_id: applicationDetails?.app_id || '',
          profanity_filter_enabled: advancedSettings.profanityFilterEnabled,
          profanity_words: advancedSettings.profanityWords,
          regex_patterns: advancedSettings.regexPatterns,
        }, REQ_ENC_KEY),
      };
      const response = await httpService.post(
        `${API_BASE_URL}/user/updateAdvancedSettings`,
        requestBody
      );
      const responseData = response.data as { status?: number; data?: any };
      if (responseData.status === 200) {
        toast.success('Advanced settings saved successfully!');
      } else {
        toast.error('Failed to save advanced settings');
      }
    } catch (error) {
      toast.error('Failed to save advanced settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setIsLoading(true);
      const payload = {
        data: await encryptResponse({
          organization_id: userDetails?.organization_id || '',
          app_id: applicationDetails?.app_id || applicationInfo.app_id,
          user_id: userDetails?.user_id || '',
          app_name: applicationInfo.name,
          is_chats_enabled: applicationInfo.is_chats_enabled,
          is_calls_enabled: applicationInfo.is_calls_enabled,
          is_notifications_enabled: applicationInfo.is_notifications_enabled,
          is_scheduled_calls_enabled: applicationInfo.is_scheduled_calls_enabled,
          is_chat_bot_enabled: applicationInfo.is_chat_bot_enabled,
          app_status: applicationInfo.app_status,
        }, REQ_ENC_KEY),
      };

      const response = await httpService.post(`${API_BASE_URL}/user/updateApplicationDetails`, payload);
      const responseData = response.data as { status?: number; data?: any };
      if (responseData && responseData.status === 200) {
        const decryptedData = await decryptResponse<any>(responseData.data, getSessionKey() as string);
        const resData = decryptedData?.application;
        if (resData) {
          console.log('resData', resData);
        dispatch(setAppName(resData.app_name));
        dispatch(setAppStatus(resData.app_status));
        dispatch(toggleChatsEnabled(resData.is_chats_enabled));
        dispatch(toggleChatBotEnabled(resData.is_chat_bot_enabled));
        dispatch(toggleCallsEnabled(resData.is_calls_enabled));
        dispatch(toggleScheduledCallsEnabled(resData.is_scheduled_calls_enabled));
        dispatch(toggleNotificationsEnabled(resData.is_notifications_enabled));
        setIsEditing(false);
        setShowSuccessAlert(true);
        toast.success('Application settings updated successfully!', { position: 'top-right' });
        setTimeout(() => setShowSuccessAlert(false), 5000);
        }
        else {
          toast.error('Failed to update application settings', { position: 'top-right' });
        }
      } else {
        toast.error('Failed to update application settings', { position: 'top-right' });
      }
    } catch (error) {
      toast.error('Failed to save application settings', { position: 'top-right' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpPopupClose = () => {
    setIsOtpPopupOpen(false);
    setOtp('');
    setOtpError('');
  };

  const sendOtpForDelete = async () => {
    const emailId = userDetails?.email_id || '';
    if (!emailId) {
      toast.error('Unable to find your email for verification.', { position: 'top-right' });
      return;
    }
    setShowDeleteDialog(false);
    setIsLoading(true);
    setLoadingMessage('Sending OTP...');
    const payload = {
      request_id: 'delete_app_otp',
      action: 'send_otp',
      data: await encryptResponse({
        email_id: emailId,
        purpose: 'mfa',
      }, REQ_ENC_KEY),
    };
    try {
      const response = await httpService.post<any>(`${API_BASE_URL}/user/sendOTP`, payload);
      if (response.data.status === 200) {
        setOtp('');
        setOtpError('');
        setIsOtpPopupOpen(true);
        toast.success('OTP sent successfully!', { position: 'top-right', autoClose: 1500 });
      } else {
        const errorMessage = response.data?.error?.errorDetails || 'Failed to send OTP. Please try again.';
        toast.error(errorMessage, { position: 'top-right', autoClose: 1500 });
      }
    } catch (error) {
      toast.error('An error occurred while sending OTP.', { position: 'top-right', autoClose: 1500 });
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtpForDelete = async () => {
    if (otp.length !== 6) {
      setOtpError('Please enter the 6-digit OTP.');
      return;
    }
    const emailId = userDetails?.email_id || '';
    const payload = {
      request_id: 'delete_app_otp',
      action: 'verify_otp',
      data: await encryptResponse({
        email_id: emailId,
        otp: otp,
      }, REQ_ENC_KEY),
    };
    setIsLoading(true);
    setLoadingMessage('Verifying OTP...');
    try {
      const response = await httpService.post<any>(`${API_BASE_URL}/user/verifyOTP`, payload);
      if (response.data.status === 200 && response.data.data) {
        const decryptedData = await decryptResponse(response.data.data, REQ_ENC_KEY);
        const is_email_verified = (decryptedData as any).is_email_verified;
        setOtpError('');
        setIsOtpPopupOpen(false);
        setOtp('');
        await handleDeleteApplication(is_email_verified);
      } else {
        setOtpError('Invalid OTP. Please try again.');
      }
    } catch (error) {
      setOtpError('An error occurred while verifying OTP.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteApplication = async (is_email_verified: string) => {
    try {
      setIsLoading(true);
      setLoadingMessage('Deleting application...');
      const payload = {
        data: await encryptResponse({
          organization_id: userDetails?.organization_id || '',
          app_id: applicationDetails?.app_id || applicationInfo.app_id,
          user_id: userDetails?.user_id || '',
          is_email_verified: is_email_verified,
        }, REQ_ENC_KEY),
      };
      const response = await httpService.post(`${API_BASE_URL}/user/deleteApplication`, payload);
      const responseData = response.data as { status: number };
      if (responseData.status === 200) {
        toast.success('Application deleted successfully!', { position: 'top-right' });
        setShowDeleteDialog(false);
        dispatch(clearApplicationDetails());
      } else {
        toast.error('Failed to delete application', { position: 'top-right' });
      }
    } catch (error) {
      toast.error('Failed to delete application', { position: 'top-right' });
    } finally {
      setIsLoading(false);
    }
  };

  const appStatuses = ['Active', 'InActive'];

  // No more render functions needed - using extracted components

  // Show loading state if applicationDetails is not loaded yet
  if (!applicationDetails && !applicationInfo.app_id) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Application Settings
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Loading application details...
        </Typography>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
              <Typography variant="body2" color="text.secondary">
                Please wait while we load your application settings...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        heading="Application Settings"
        subheading="Configure your application settings, security preferences, and advanced options"
        variant="gradient"
      >
        <Chip
          label={`Role: ${userRole}`}
          color="secondary"
          variant="filled"
          size="small"
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            color: '#ffffff',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
            }
          }}
        />
      </PageHeader>

      {/* Tab Navigation */}
      <ApplicationTabNavigation
        activeTab={activeTab}
        onTabChange={setActiveTab}
        permissionUtils={permissionUtils}
      />

      {/* Tab Content */}
      <Box>
        {activeTab === 'general' && canAccessSection('general') && (
          <GeneralSettings
            applicationInfo={applicationInfo}
            setApplicationInfo={setApplicationInfo}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            isLoading={isLoading}
            showSuccessAlert={showSuccessAlert}
            appStatuses={appStatuses}
            onSave={handleSaveSettings}
            onDeleteApplication={() => setShowDeleteDialog(true)}
            permissionUtils={permissionUtils}
          />
        )}

        {activeTab === 'theme' && canAccessSection('theme') && (
          <ThemeSettings
            themeSettings={themeSettings}
            setThemeSettings={setThemeSettings}
            onSave={handleSaveTheme}
            permissionUtils={permissionUtils}
          />
        )}

        {activeTab === 'privacy' && canAccessSection('privacy') && (
          <DataPrivacySettingsComponent
            dataPrivacySettings={dataPrivacySettings}
            setDataPrivacySettings={setDataPrivacySettings}
            onSave={handleSaveDataPrivacy}
            permissionUtils={permissionUtils}
          />
        )}

        {activeTab === 'security' && canAccessSection('security') && (
          <SecuritySettings
            securitySettings={securitySettings}
            setSecuritySettings={setSecuritySettings}
            onSave={handleSaveSecurity}
            permissionUtils={permissionUtils}
          />
        )}

        {activeTab === 'storage' && canAccessSection('storage') && (
          <StorageSettings
            organizationId={userDetails?.organization_id || ''}
            applicationId={applicationDetails?.app_id || applicationInfo.app_id}
            permissionUtils={permissionUtils}
          />
        )}

        {activeTab === 'advanced' && canAccessSection('advanced') && (
          <AdvancedSettingsComponent
            advancedSettings={advancedSettings}
            setAdvancedSettings={setAdvancedSettings}
            permissionUtils={permissionUtils}
            onSave={handleSaveAdvanced}
            isLoading={isLoading}
          />
        )}

        {/* Show access denied message if user tries to access restricted section */}
        {(!canAccessSection(activeTab as any)) && (
          <Alert severity="warning">
            <Typography variant="h6">Access Denied</Typography>
            <Typography variant="body2">
              You don't have permission to access this section. Contact your administrator if you need access.
            </Typography>
          </Alert>
        )}
      </Box>

      {/* Delete Application Dialog */}
      <DeleteApplicationDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirmDelete={sendOtpForDelete}
        isLoading={isLoading}
        permissionUtils={permissionUtils}
      />

      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
        open={isLoading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {loadingMessage}
          </Typography>
        </Box>
      </Backdrop>

      <Dialog
        open={isOtpPopupOpen}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              width: '500px',
              maxWidth: '90vw',
            },
          },
        }}
      >
        <DialogTitle>Verify Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Enter the 6-digit OTP sent to <strong>{userDetails?.email_id}</strong> to delete this application.
          </Typography>
          <OtpInput
            id="otp"
            value={otp}
            onChange={(value) => {
              setOtp(value);
              if (otpError) {
                setOtpError('');
              }
            }}
            error={!!otpError}
            helperText={otpError}
            autoFocus={isOtpPopupOpen}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOtpPopupClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={verifyOtpForDelete} color="primary" disabled={otp.length !== 6}>
            Verify OTP
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApplicationSettings;
